import React, { useCallback } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import {
	FireScreenAnalytics,
	ContextualAnalyticsData,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import { X2C_SCREENS } from '../../common/constants/metrics';
import type { CSVReviewTilesType } from '../../common/constants/steps';
import { StepFooterStandard } from '../../common/ui/step-footer';
import { ProgressBarStandard } from '../../common/ui/step-progress-bar';
import { StepScreen } from '../../common/ui/step-screen';
import { useProjectData } from '../../controllers/import-state';
import { useStartImportController } from '../../controllers/start-import';
import { useProductSteps } from '../../controllers/utils';
import { DownloadConfiguration } from './download-configuration';
import messages from './messages';
import { ReviewTiles } from './review-tiles';

type Props = {
	onNext: () => void;
	onReturn: () => void;
	onCloseAndReturnToUploadStep: () => void;
	isCompact?: boolean;
	tiles: CSVReviewTilesType[];
	shouldStartImportOnNext: boolean;
};
export const ReviewScreenStandard = ({
	onReturn,
	onNext,
	onCloseAndReturnToUploadStep,
	isCompact,
	tiles,
	shouldStartImportOnNext,
}: Props) => {
	const { productSteps, currentStepIndex } = useProductSteps();
	const { formatMessage } = useIntl();
	const { startImport, isImportLoading, isStartImportDisabled, hasImportErrorOccurred } =
		useStartImportController();
	const [projectData] = useProjectData();

	const handleSubmit = useCallback(() => {
		startImport({ projectData, onNext });
	}, [onNext, projectData, startImport]);

	return (
		<ContextualAnalyticsData sourceName={X2C_SCREENS.REVIEW_SCREEN_STANDARD} sourceType={SCREEN}>
			<StepScreen
				testId="project-create-importer-upload-flow.ui.review-screen-standard"
				renderProgressBar={
					!isCompact && (
						<ProgressBarStandard
							productSteps={productSteps}
							currentStepIndex={currentStepIndex}
							onClose={onCloseAndReturnToUploadStep}
						/>
					)
				}
				header={formatMessage(messages.heading)}
				description={formatMessage(messages.description)}
				maxWidth="720px"
			>
				<ReviewTiles tiles={tiles} />
				{fg('enable_x2c_save_configuration') && <DownloadConfiguration />}
				<StepFooterStandard
					alignRight
					isFixedPosition={!isCompact}
					onReturn={onReturn}
					nextButtonType="submit"
					onClickNext={handleSubmit}
					shouldStartImportOnNext={shouldStartImportOnNext}
					isLoading={isImportLoading}
					shouldDisableReturn={isImportLoading}
					shouldDisableNext={isStartImportDisabled}
					hasImportErrorOccurred={hasImportErrorOccurred}
				/>
				<FireScreenAnalytics />
			</StepScreen>
		</ContextualAnalyticsData>
	);
};
